/**
 * Timeline Page
 *
 * Displays deployment history as a timeline chart with filters.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TimelineChart } from '@brutal/brutal/organisms';
import type { TimelineDeployment, TimelineRange } from '@brutal/brutal/organisms';
import { useAgent } from '../hooks/useAgent';
import { useDashboardStore } from '../stores/dashboard';

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export function Timeline() {
  const navigate = useNavigate();
  const { isConnected, listDeployments } = useAgent();
  const { deployments, setDeployments } = useDashboardStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<{
    environment: string | null;
    artifact: string | null;
    timeRange: '1h' | '6h' | '24h' | '7d';
  }>({
    environment: null,
    artifact: null,
    timeRange: '24h',
  });

  // Fetch deployments
  useEffect(() => {
    if (!isConnected) {
      setLoading(true);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listDeployments();
        setDeployments(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch deployments');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isConnected, listDeployments, setDeployments]);

  // Transform deployments to timeline format
  const filteredDeployments: TimelineDeployment[] = deployments
    .filter((d) => {
      if (filter.environment && d.environment !== filter.environment) return false;
      if (filter.artifact && d.artifact !== filter.artifact) return false;

      // Time filter
      const deployTime = new Date(d.startedAt).getTime();
      const now = Date.now();
      const ranges: Record<string, number> = {
        '1h': 3600000,
        '6h': 21600000,
        '24h': 86400000,
        '7d': 604800000,
      };
      const maxRange = ranges[filter.timeRange];
      if (maxRange !== undefined && now - deployTime > maxRange) return false;

      return true;
    })
    .map((d) => ({
      id: d.id,
      artifact: d.artifact,
      version: d.version,
      environment: d.environment,
      status: d.status,
      startedAt: new Date(d.startedAt).getTime(),
      completedAt: d.completedAt ? new Date(d.completedAt).getTime() : null,
    }));

  // Calculate time range for chart
  const timeRange: TimelineRange = (() => {
    if (filteredDeployments.length === 0) {
      const now = Date.now();
      return { start: now - 86400000, end: now };
    }
    const times = filteredDeployments.map((d) => d.startedAt);
    const minTime = Math.min(...times);
    const maxTime = Math.max(...times, Date.now());
    // Add 10% padding
    const padding = (maxTime - minTime) * 0.1 || 3600000;
    return { start: minTime - padding, end: maxTime + padding };
  })();

  // Get unique values for filters
  const environments = [...new Set(deployments.map((d) => d.environment))];
  const artifacts = [...new Set(deployments.map((d) => d.artifact))];

  const handleEventClick = useCallback(
    (eventId: string) => {
      navigate(`/deployments/${eventId}`);
    },
    [navigate],
  );

  // Select styles
  const selectStyle = {
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '4px',
    padding: '8px 12px',
    color: '#00ff88',
    fontFamily: 'monospace',
  };

  if (!isConnected) {
    return (
      <div>
        <h2 style={{ fontSize: '2rem', marginBottom: '20px' }}>Timeline</h2>
        <p style={{ color: '#888' }}>Connecting to agent...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ fontSize: '2rem', marginBottom: '20px' }}>Deployment Timeline</h2>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '20px',
          flexWrap: 'wrap',
        }}
      >
        <select
          value={filter.environment ?? ''}
          onChange={(e) => setFilter((f) => ({ ...f, environment: e.target.value || null }))}
          style={selectStyle}
        >
          <option value="">All Environments</option>
          {environments.map((env) => (
            <option key={env} value={env}>
              {env}
            </option>
          ))}
        </select>

        <select
          value={filter.artifact ?? ''}
          onChange={(e) => setFilter((f) => ({ ...f, artifact: e.target.value || null }))}
          style={selectStyle}
        >
          <option value="">All Artifacts</option>
          {artifacts.map((art) => (
            <option key={art} value={art}>
              {art}
            </option>
          ))}
        </select>

        <select
          value={filter.timeRange}
          onChange={(e) =>
            setFilter((f) => ({ ...f, timeRange: e.target.value as typeof filter.timeRange }))
          }
          style={selectStyle}
        >
          <option value="1h">Last 1 hour</option>
          <option value="6h">Last 6 hours</option>
          <option value="24h">Last 24 hours</option>
          <option value="7d">Last 7 days</option>
        </select>
      </div>

      {/* Chart */}
      {loading ? (
        <p style={{ color: '#888' }}>Loading...</p>
      ) : error ? (
        <p style={{ color: '#ff4444' }}>Error: {error}</p>
      ) : filteredDeployments.length === 0 ? (
        <p style={{ color: '#666' }}>No deployments in selected time range.</p>
      ) : (
        <div style={{ backgroundColor: '#1a1a1a', borderRadius: '8px', padding: '20px' }}>
          <TimelineChart
            deployments={filteredDeployments}
            range={timeRange}
            width={900}
            height={Math.max(200, environments.length * 80 + 100)}
            onDeploymentClick={handleEventClick}
          />
        </div>
      )}

      {/* Legend */}
      <div style={{ marginTop: '20px', display: 'flex', gap: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: '#00ff88',
            }}
          />
          <span style={{ color: '#888', fontSize: '0.875rem' }}>Deployed</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: '#ffaa00',
            }}
          />
          <span style={{ color: '#888', fontSize: '0.875rem' }}>In Progress</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: '#ff4444',
            }}
          />
          <span style={{ color: '#888', fontSize: '0.875rem' }}>Failed</span>
        </div>
      </div>
    </div>
  );
}
